import api from '../../../shared/utils/api';
import type {
  CompanyContext,
  AutoGenerateStatus,
  InterviewOutput,
} from '../../../types/companyContext';

/** Política de follow-up do agente por tenant (decisões do dono 8A/9A/10A). */
export interface FollowupPolicy {
  enabled: boolean;
  max_attempts: number;
  interval_hours: number;
  window: { days: number[]; start: number; end: number };
  daily_cap: number;
  /** Opcional: sem limite diário nos lembretes de abertura do Outbound (opção C do dono, 23/10). */
  opening_resend_uncapped?: boolean;
}

export interface FollowupPolicyResponse {
  followup_policy: FollowupPolicy | null;
  configured: boolean;
}

/** Calendário de feriados por tenant (decisão do dono 1-C, 2026-10-11). */
export interface HolidaysPolicy {
  use_national: boolean;
  extra: string[];
  exclude: string[];
}

export interface NationalHoliday {
  date: string;
  name: string;
}

export interface HolidaysResponse {
  holidays: HolidaysPolicy | null;
  configured: boolean;
  national_holidays: NationalHoliday[];
}

export const companyContextApi = {
  getHolidays: async (): Promise<HolidaysResponse> => {
    const { data } = await api.get<HolidaysResponse>('/company-context/holidays');
    return data;
  },

  saveHolidays: async (policy: Partial<HolidaysPolicy>): Promise<HolidaysResponse> => {
    const { data } = await api.put<HolidaysResponse>('/company-context/holidays', policy);
    return data;
  },

  getFollowupPolicy: async (): Promise<FollowupPolicyResponse> => {
    const { data } = await api.get<FollowupPolicyResponse>('/company-context/followup-policy');
    return data;
  },

  saveFollowupPolicy: async (policy: Partial<FollowupPolicy>): Promise<FollowupPolicyResponse> => {
    const { data } = await api.put<FollowupPolicyResponse>('/company-context/followup-policy', policy);
    return data;
  },

  getContext: async (): Promise<CompanyContext> => {
    const { data } = await api.get<CompanyContext>('/company-context');
    return data;
  },

  saveContext: async (context: Partial<CompanyContext>): Promise<CompanyContext> => {
    const { data } = await api.put<CompanyContext>('/company-context', context);
    return data;
  },

  getInterviewOutput: async (): Promise<InterviewOutput | null> => {
    try {
      const { data } = await api.get<InterviewOutput>('/company-context/interview-output');
      return data;
    } catch (err: unknown) {
      if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
      throw err;
    }
  },

  triggerAutoGenerate: async (
    companyName: string,
    domain?: string,
  ): Promise<{ status: string }> => {
    const { data } = await api.post('/company-context/auto-generate', {
      company_name: companyName,
      domain,
    });
    return data;
  },

  getAutoGenerateStatus: async (): Promise<AutoGenerateStatus> => {
    const { data } = await api.get<AutoGenerateStatus>(
      '/company-context/auto-generate/status',
    );
    return data;
  },
};
